<template>
  <div v-if="canUseAssistant">
    <!-- Only on pages without the dashboard sidebar; there its Help items open the panel.
         Stacked above the "Report a problem" button (3.25rem tall, 1.25rem from the bottom). -->
    <button
      v-if="!hasSidebar"
      v-show="!isOpen"
      type="button"
      class="assistant-trigger"
      aria-haspopup="dialog"
      @click="open"
    >
      <SparklesIcon class="h-6 w-6 shrink-0" aria-hidden="true" />
      <!-- Icon only; the label fades in beside it on hover / keyboard focus and still names the button. -->
      <span class="assistant-trigger__label">Ask Meredith</span>
    </button>

    <section
      v-if="isOpen"
      class="assistant-panel"
      role="dialog"
      aria-label="Meredith, performance assistant"
      @keydown.esc="close"
    >
      <header class="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <div class="flex items-center gap-2">
          <SparklesIcon class="h-4 w-4 text-gray-500" aria-hidden="true" />
          <h2 class="text-sm font-semibold text-gray-800">Meredith</h2>
          <span class="text-xs text-gray-500">Performance assistant</span>
        </div>
        <button
          type="button"
          class="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          aria-label="Close assistant"
          @click="close"
        >
          <XMarkIcon class="h-5 w-5" aria-hidden="true" />
        </button>
      </header>

      <div ref="messageList" class="flex-1 overflow-y-auto px-4 py-3" aria-live="polite">
        <div v-if="!messages.length" class="flex flex-col gap-2 text-sm text-gray-600">
          <p>
            Hi, I'm Meredith. Ask me about signups, PSA, M1/M2 rates and donations for your offices
            and campaigns, or how to do something in the platform. For example:
          </p>
          <button
            v-for="example in exampleQuestions"
            :key="example"
            type="button"
            class="rounded-md border border-gray-200 px-3 py-2 text-left text-gray-700 hover:bg-gray-50"
            @click="send(example)"
          >
            {{ example }}
          </button>
        </div>

        <ul v-else class="flex flex-col gap-3">
          <li
            v-for="(message, index) in messages"
            :key="index"
            :class="[
              'max-w-[85%] whitespace-pre-line rounded-lg px-3 py-2 text-sm',
              message.role === 'user'
                ? 'self-end bg-gray-800 text-white'
                : message.error
                ? 'self-start bg-red-50 text-red-700'
                : 'self-start bg-gray-100 text-gray-800',
            ]"
          >
            {{ message.text }}
          </li>
          <li
            v-if="sending"
            class="self-start rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-500"
          >
            Looking that up…
          </li>
        </ul>
      </div>

      <form
        class="flex items-end gap-2 border-t border-gray-200 px-3 pt-3 pb-1"
        @submit.prevent="send()"
      >
        <label for="assistant-question" class="sr-only">Your question</label>
        <textarea
          id="assistant-question"
          ref="questionField"
          v-model="question"
          rows="2"
          :maxlength="maxQuestionLength"
          :disabled="sending"
          placeholder="e.g. How many signups did Austin have last week?"
          class="input--text w-full resize-none text-sm"
          @keydown.enter.exact.prevent="send()"
        />
        <button
          type="submit"
          class="shrink-0 rounded-md bg-gray-800 p-2 text-white hover:bg-gray-700 disabled:opacity-50"
          :disabled="!canSend"
          aria-label="Send question"
        >
          <PaperAirplaneIcon class="h-5 w-5" aria-hidden="true" />
        </button>
      </form>
      <p class="px-3 pb-2 text-xs text-gray-500">
        Don't enter card numbers or donor details. Use a pledge ID for donation questions.
      </p>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { PaperAirplaneIcon, SparklesIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { useHelpWidgets } from '@/composables/helpWidgets'
import {
  askAssistant,
  CARD_NUMBER_ERROR,
  containsCardNumber,
  EXAMPLE_QUESTIONS,
  MAX_QUESTION_LENGTH,
  NAME_ANSWER,
  NAME_QUESTION,
} from '@/services/assistant'

export default {
  name: 'AssistantChatWidget',
  components: {
    PaperAirplaneIcon,
    SparklesIcon,
    XMarkIcon,
  },
  setup() {
    // Open state is shared so the sidebar's Help item can open the panel too.
    const { assistantOpen, canUseAssistant, hasSidebar } = useHelpWidgets()
    return { isOpen: assistantOpen, canUseAssistant, hasSidebar }
  },
  data() {
    return {
      sending: false,
      question: '',
      // { role: 'user' | 'assistant', text, error? } -- kept for this page session only.
      messages: [],
      exampleQuestions: EXAMPLE_QUESTIONS,
      maxQuestionLength: MAX_QUESTION_LENGTH,
    }
  },
  computed: {
    ...mapGetters(['isLoggedIn']),
    canSend() {
      return !this.sending && Boolean(this.question.trim())
    },
    history() {
      // Answered exchanges only (failed ones aren't worth paying to resend), oldest first.
      const turns = []
      this.messages.forEach((message, index) => {
        const reply = this.messages[index + 1]
        if (message.role === 'user' && reply?.role === 'assistant' && !reply.error) {
          turns.push({ question: message.text, answer: reply.text })
        }
      })
      return turns
    },
  },
  watch: {
    isOpen(open) {
      if (!open) return
      this.$nextTick(() => {
        this.$refs.questionField?.focus()
        this.scrollToBottom()
      })
    },
    isLoggedIn(loggedIn) {
      // Never carry one user's conversation over to the next login on a shared device.
      if (!loggedIn) {
        this.messages = []
        this.isOpen = false
      }
    },
  },
  methods: {
    open() {
      this.isOpen = true
    },
    close() {
      this.isOpen = false
    },
    scrollToBottom() {
      const list = this.$refs.messageList
      if (list) list.scrollTop = list.scrollHeight
    },
    async send(text) {
      const question = (text ?? this.question).trim()
      if (this.sending || !question) return
      if (containsCardNumber(question)) {
        // Never send it: the server would refuse too, but this way it doesn't leave the browser.
        this.messages.push({ role: 'assistant', text: CARD_NUMBER_ERROR, error: true })
        this.$nextTick(this.scrollToBottom)
        return
      }

      // Captured before this question is added, so it isn't sent twice.
      const history = this.history
      this.messages.push({ role: 'user', text: question })
      this.question = ''

      if (question.toLowerCase() === NAME_QUESTION.toLowerCase()) {
        this.messages.push({ role: 'assistant', text: NAME_ANSWER })
        this.$nextTick(this.scrollToBottom)
        return
      }
      this.sending = true
      this.$nextTick(this.scrollToBottom)

      const { ok, answer, error } = await askAssistant(question, history)
      this.messages.push({ role: 'assistant', text: ok ? answer : error, error: !ok })
      this.sending = false
      this.$nextTick(() => {
        this.scrollToBottom()
        this.$refs.questionField?.focus()
      })
    },
  },
}
</script>

<style scoped>
.assistant-trigger {
  @apply fixed z-20 flex h-[3.25rem] w-[3.25rem] items-center justify-center rounded-full border
    border-gray-200 bg-white text-sm font-medium text-gray-700 shadow-lg hover:bg-gray-50
    focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2;
  right: 1.25rem;
  bottom: 5.25rem;
  transition: background-color 150ms ease;
}

.assistant-trigger__label {
  @apply pointer-events-none absolute right-full top-1/2 mr-2 whitespace-nowrap rounded-full border
    border-gray-200 bg-white px-3 py-1.5 opacity-0 shadow-md;
  transform: translate(0.5rem, -50%);
  transition: opacity 200ms ease, transform 200ms ease;
}

.assistant-trigger:hover .assistant-trigger__label,
.assistant-trigger:focus-visible .assistant-trigger__label {
  @apply opacity-100;
  transform: translate(0, -50%);
}

.assistant-panel {
  /* Above both triggers (z-20), below the app's dialogs (z-50). */
  @apply fixed bottom-5 right-5 z-40 flex h-[32rem] w-[24rem] max-w-[calc(100vw-2.5rem)] flex-col
    overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl;
  max-height: calc(100vh - 2.5rem);
  /* dvh tracks mobile browser toolbars, so the input stays on screen in portrait and landscape. */
  max-height: calc(100dvh - 2.5rem);
}

/* Phones: a sheet across the bottom of the screen with a small gutter. */
@media (max-width: 639px) {
  .assistant-panel {
    @apply inset-x-3 bottom-3 w-auto max-w-none;
    height: min(32rem, calc(100vh - 1.5rem));
    height: min(32rem, calc(100dvh - 1.5rem));
    max-height: none;
  }
}

/* Phones: smaller, so it covers less of the page. */
@media (max-width: 639px) {
  .assistant-trigger {
    @apply right-3 h-11 w-11;
    bottom: 4rem;
  }

  .assistant-trigger svg {
    @apply h-5 w-5;
  }
}

@media (prefers-reduced-motion: reduce) {
  .assistant-trigger__label {
    transition: none;
  }
}

@media print {
  .assistant-trigger,
  .assistant-panel {
    display: none;
  }
}
</style>
